/**
 * Confirmation. "You're booked" only when the visit was written (or is being
 * written) to the schedule; otherwise "Request received, we'll text you to
 * confirm" (OB-13). Calendar file, directions, and a second family member
 * back to back (OB-20).
 */
import type { PublicSite, SubmitResult } from '@velora/contracts/booking';
import { Button } from '@velora/ui';
import { CalendarPlus, CheckCircle2, Hourglass, MapPin, Phone, UserPlus } from 'lucide-react';
import { telHref, timeLabel, whenLabel } from '../lib/format.js';

interface Props {
  site: PublicSite;
  result: SubmitResult;
  booked: SubmitResult[];
  phoneLabel: string | null;
  onBookAnother: () => void;
}

/** Saves the ICS body as a file the visitor opens in their calendar app. */
export function downloadIcs(result: SubmitResult): void {
  const blob = new Blob([result.ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dental-visit-${result.reference}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function mapsHref(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export function DoneStep({ site, result, booked, phoneLabel, onBookAnother }: Props) {
  const tz = site.timezone;
  const confirmed = result.status === 'confirmed';
  const others = booked.filter((b) => b.reference !== result.reference);
  return (
    <div className="bw-step bw-done" data-testid="step-done" data-status={result.status}>
      <div className="bw-done__hero" data-tone={confirmed ? 'success' : 'info'}>
        <span className="bw-done__icon" aria-hidden>
          {confirmed ? <CheckCircle2 size={28} /> : <Hourglass size={26} />}
        </span>
        <h2 className="bw-done__title" tabIndex={-1} data-autofocus>
          {confirmed ? 'You are booked' : 'Request received'}
        </h2>
        <p className="bw-done__msg">{result.message}</p>
      </div>

      <dl className="bw-summary" aria-label="Visit details">
        <div>
          <dt>When</dt>
          <dd>
            <strong>{whenLabel(result.start, tz, site.today)}</strong>
            <span className="bw-dim"> to {timeLabel(result.end, tz)}</span>
          </dd>
        </div>
        <div>
          <dt>Visit</dt>
          <dd>
            {result.typeLabel}
            <span className="bw-dim"> with {result.providerName}</span>
          </dd>
        </div>
        <div>
          <dt>Where</dt>
          <dd>
            {result.officeName}
            {result.address ? <span className="bw-dim bw-block">{result.address}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Reference</dt>
          <dd className="vel-num" data-testid="reference">
            {result.reference}
          </dd>
        </div>
      </dl>

      {others.length > 0 ? (
        <div className="bw-also">
          <p className="bw-q">Also booked today</p>
          <ul>
            {others.map((b) => (
              <li key={b.reference}>
                {whenLabel(b.start, tz, site.today)} · {b.typeLabel}{' '}
                <span className="bw-dim">
                  ({b.status === 'confirmed' ? 'booked' : 'requested'}, {b.reference})
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="bw-done__actions">
        <Button
          variant="secondary"
          size="lg"
          leadingIcon={<CalendarPlus size={18} />}
          onClick={() => downloadIcs(result)}
        >
          Add to calendar
        </Button>
        {result.address ? (
          <Button asChild variant="secondary" size="lg">
            <a href={mapsHref(result.address)} target="_blank" rel="noreferrer">
              <MapPin size={18} aria-hidden /> Directions
            </a>
          </Button>
        ) : null}
        {result.officePhone ? (
          <Button asChild variant="ghost" size="lg">
            <a href={telHref(result.officePhone)}>
              <Phone size={18} aria-hidden /> {phoneLabel ?? 'Call the office'}
            </a>
          </Button>
        ) : null}
      </div>

      <button
        type="button"
        className="bw-another"
        onClick={onBookAnother}
        data-testid="book-another"
      >
        <span className="bw-another__icon" aria-hidden>
          <UserPlus size={20} />
        </span>
        <span>
          <span className="bw-another__label">Book another family member</span>
          <span className="bw-dim bw-block">
            We will suggest times right before or after this visit.
          </span>
        </span>
      </button>
    </div>
  );
}
